import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { BroadcastMessage } from '@app/models/broadcast-message';
import { AppTileService } from '@app/services/app-tile.service';
import { CacheService } from '@app/services/cache.service';
import { UserAppTileService } from '@app/services/user-app-tile.service';
import { Environment } from '@environments/interface.environment';
import { isAfter, isBefore } from 'date-fns';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class BroadcastService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private http: HttpClient,
              private cache: CacheService,
              private httpHelper: HttpHelper,
              private appTileService: AppTileService,
              private userAppTileService: UserAppTileService) { }

  private readonly cacheKey = 'BroadcastService-getBroadcastMessages';
  public useCache = true;
  private reload$ = new Subject<void>();
  public refresh$ = new BehaviorSubject(null);

  getActiveBroadcastMessages$: Observable<Array<BroadcastMessage>>;
  getMyHiddenBroadcastMessages$: Observable<BroadcastMessage[]>;
  getAllBroadcastMessages$: Observable<Array<BroadcastMessage>>;

  public reload() {
    this.getActiveBroadcastMessages$ = null;
    this.getAllBroadcastMessages$ = null;
    this.getMyHiddenBroadcastMessages$ = null;
    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKey);
    }
    this.reload$.next();
    this.refresh$.next(null);
  }

  getAllBroadcastMessages(): Observable<Array<BroadcastMessage>> {
    if (this.getAllBroadcastMessages$) {
      return this.getAllBroadcastMessages$;
    } else {
      this.getAllBroadcastMessages$ = this.http.get<Array<BroadcastMessage>>(`${Constants.API_ENDPOINT}/broadcastmessages/all`)
        .pipe(
          map(response => {
            console.log(`fetched result getAllBroadcastMessages: `, response);
            return response.map(p => new BroadcastMessage(p));
          }),
          tap(data => {
            this.useCache && this.cache.set(`${this.cacheKey}-all`, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAllBroadcastMessages', result: new Array<BroadcastMessage>(), cacheKey: `${this.cacheKey}-all` })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<BroadcastMessage>>(`${this.cacheKey}-all`);
        if (cachedData) {
          return concat(of(cachedData), this.getAllBroadcastMessages$);
        }
      }
      return this.getAllBroadcastMessages$;
    }
  }

  getActiveBroadcastMessages(): Observable<Array<BroadcastMessage>> {
    if (this.getActiveBroadcastMessages$) {
      return this.getActiveBroadcastMessages$;
    } else {
      this.getActiveBroadcastMessages$ = this.http.get<Array<BroadcastMessage>>(`${Constants.API_ENDPOINT}/broadcastmessages`, {

      })
        .pipe(
          map(response => {
            console.log(`fetched result getActiveBroadcastMessages: `, response);
            return response.map(p => new BroadcastMessage(p)).filter(b => (!b.EndDateAsDate || isAfter(b.EndDateAsDate, new Date())) && (!b.StartDateAsDate || isBefore(b.StartDateAsDate, new Date())));
          }),
          tap(data => {
            this.useCache && this.cache.set(this.cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getBroadcastMessages', result: new Array<BroadcastMessage>(), cacheKey: this.cacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<BroadcastMessage>>(this.cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getActiveBroadcastMessages$);
        }
      }
      return this.getActiveBroadcastMessages$;
    }
  }

  getBroadcastMessage(id: number): Observable<BroadcastMessage> {
    return this.http.get<BroadcastMessage>(`${Constants.API_ENDPOINT}/broadcastmessages/${id}`)
      .pipe(
        map(response => {
          console.log(`fetched result getBroadcastMessage: `, response);
          return new BroadcastMessage(response);
        }));
  }

  getMyHiddenBroadcastMessages(): Observable<Array<BroadcastMessage>> {
    if (this.getMyHiddenBroadcastMessages$) {
      return this.getMyHiddenBroadcastMessages$;
    } else {
      this.getMyHiddenBroadcastMessages$ = this.http.get<Array<BroadcastMessage>>(`${Constants.API_ENDPOINT}/me/broadcastmessages/hidden`)
        .pipe(
          map(response => {
            console.log(`fetched result getMyHiddenBroadcastMessages: `, response);
            return response.map(p => new BroadcastMessage(p)).filter(b => (!b.EndDateAsDate || isAfter(b.EndDateAsDate, new Date())) && (!b.StartDateAsDate || isBefore(b.StartDateAsDate, new Date())));
          }),
          tap(data => {
            this.useCache && this.cache.set(`${this.cacheKey}-hidden`, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getMyHiddenBroadcastMessages', result: new Array<BroadcastMessage>() })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<BroadcastMessage>>(`${this.cacheKey}-hidden`);
        if (cachedData) {
          return concat(of(cachedData), this.getMyHiddenBroadcastMessages$);
        }
      }
      return this.getMyHiddenBroadcastMessages$;
    }
  }

  hideBroadcastMessage(id: number): Observable<any> {
    const updateOperation = this.http.put<BroadcastMessage>(`${Constants.API_ENDPOINT}/me/broadcastmessages/${id}/hide`, {})
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return response;
        }));

    return updateOperation;
  }

  updateBroadcastMessage(id: number, broadcast: BroadcastMessage): Observable<BroadcastMessage> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<BroadcastMessage>(`${Constants.API_ENDPOINT}/broadcastmessages/${id}`, broadcast, {
      params: httpParams
    })
      .pipe(
        map(response => {
          // if the broad cast message is targeted to app then clear the app tile service cache key from local storage
          const result = broadcast as BroadcastMessage;
          if (result.AppTileBroadcastMessages.length > 0) {
            this.appTileService.reload();
            this.userAppTileService.reload();
          }
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return broadcast;
        }));

    return fetchOperation;
  }

  deleteBroadcastMessage(id: number): Observable<BroadcastMessage> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<BroadcastMessage>(`${Constants.API_ENDPOINT}/broadcastmessages/${id}`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          const result = response as BroadcastMessage;
          if (result.AppTileBroadcastMessages.length > 0) {
            this.appTileService.reload();
            this.userAppTileService.reload();
          }
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteBroadcastMessage', result: new BroadcastMessage() })));

    return fetchOperation;
  }

  addBroadcastMessage(broadcast: BroadcastMessage): Observable<BroadcastMessage> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.post<BroadcastMessage>(`${Constants.API_ENDPOINT}/broadcastmessages`, broadcast, {
      params: httpParams
    })
      .pipe(
        map(response => {
          // if the broad cast message is targeted to app then clear the app key from local storage
          const result = response as BroadcastMessage;
          if (result.AppTileBroadcastMessages.length > 0) {
            this.appTileService.reload();
            this.userAppTileService.reload();
          }
          console.log(`added result: `);
          console.log(response);
          this.reload();
          return new BroadcastMessage(response);
        }));

    return fetchOperation;
  }
}
